/**
 * Holidays: the pure rules behind the Holidays pages (list, create, bulk
 * upload, edit, status). Ported from the applause comms-app's holidays
 * feature, which comms.bhanzu.com served before Comms V2 (BHAN-2774).
 *
 * Time convention, read from production CommHolidays rows (2026-10-12):
 * `holiday_date` is the epoch second of 00:00 IST on the holiday, and each
 * event's `event_time` is `holiday_date - days * 86400 + <comms time in IST>`
 * (Diwali 2026: 8 Nov 00:00 IST, comms 5 Nov 09:00 IST and 7 Nov 09:00 IST).
 * job-holiday-reminder sends on the UTC day an `event_time` falls in. The
 * applause app reached these numbers through the browser's clock (correct
 * only in an IST browser); here they are computed from the date and time
 * strings, so the result does not depend on the browser's time zone.
 */
import { canManageComms, commsManagerRole } from './permissions'
import { eventTypeError, normalizeEventType } from './eventType'
import { IST_OFFSET_SECONDS, istIsoDate, istTime } from '../utils/datetime'

export { IST_OFFSET_SECONDS }
const DAY = 86400

/** Comms may go out 1 to 7 days before the holiday (the applause rule). */
export const MAX_COMMS_DAYS = 7
/** The comms time picker in the applause app allowed 07:00 to 23:59. */
export const EARLIEST_COMMS_TIME = '07:00'

export const STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    COMPLETED: 'completed',
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const DMY_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
const TIME_RE = /^(\d{1,2}):(\d{2})$/

/** True for a real calendar date (no 31 Feb). */
const validDate = (y, m, d) => {
    const t = new Date(Date.UTC(y, m - 1, d))
    return (
        t.getUTCFullYear() === y &&
        t.getUTCMonth() === m - 1 &&
        t.getUTCDate() === d
    )
}

/**
 * Epoch second of 00:00 IST on `date` ("YYYY-MM-DD"), or null if invalid.
 *
 * @param {string} date
 * @returns {number|null}
 */
export const istMidnightEpoch = (date) => {
    const m = DATE_RE.exec(date || '')
    if (!m) return null
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
    if (!validDate(y, mo, d)) return null
    return Date.UTC(y, mo - 1, d) / 1000 - IST_OFFSET_SECONDS
}

/** "DD/MM/YYYY" (the bulk CSV format) to "YYYY-MM-DD", or null. */
export const dmyToIso = (value) => {
    const m = DMY_RE.exec((value || '').trim())
    if (!m) return null
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])]
    if (!validDate(y, mo, d)) return null
    return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** Seconds after midnight for "HH:MM", or null. */
export const timeToSeconds = (value) => {
    const m = TIME_RE.exec((value || '').trim())
    if (!m) return null
    const [h, min] = [Number(m[1]), Number(m[2])]
    if (h > 23 || min > 59) return null
    return h * 3600 + min * 60
}

/** "YYYY-MM-DD" in IST for an epoch second ('' for none). */
export const isoDateIst = istIsoDate

/** "HH:MM" in IST for an epoch second ('' for none). */
export const timeIst = istTime

/**
 * The `events` list for a holiday: the comms event, then the reminder if any.
 *
 * @param {object} args
 * @param {number} args.holidayDate   epoch of 00:00 IST on the holiday
 * @param {number} args.commsSeconds  comms time of day, seconds after midnight IST
 * @param {number} args.commsDays     days before the holiday
 * @param {number|null} [args.reminderDays]  days before the holiday, or none
 */
export const buildEvents = ({
    holidayDate,
    commsSeconds,
    commsDays,
    reminderDays = null,
}) => {
    const event = (days) => ({
        event_time: holidayDate - Number(days) * DAY + commsSeconds,
        days_before_holiday: Number(days),
    })
    const events = [event(commsDays)]
    if (
        reminderDays !== null &&
        reminderDays !== '' &&
        reminderDays !== undefined
    )
        events.push(event(reminderDays))
    return events
}

/** An empty create form. */
export const emptyHolidayForm = () => ({
    holiday_id: '',
    holiday_name: '',
    holiday_date: '',
    comms_days: '1',
    comms_time: EARLIEST_COMMS_TIME,
    reminder_on: false,
    reminder_days: '1',
    allowed_countries: [],
})

/** A stored holiday as an edit form. */
export const holidayToForm = (holiday) => {
    const [comms, reminder] = holiday?.events ?? []
    return {
        holiday_id: holiday?.holiday_id ?? '',
        holiday_name: holiday?.holiday_name ?? '',
        holiday_date: holiday?.holiday_date
            ? isoDateIst(holiday.holiday_date)
            : '',
        comms_days: comms ? String(comms.days_before_holiday) : '1',
        comms_time: comms ? timeIst(comms.event_time) : EARLIEST_COMMS_TIME,
        reminder_on: !!reminder,
        reminder_days: reminder ? String(reminder.days_before_holiday) : '1',
        allowed_countries: holiday?.allowed_countries ?? [],
        event_status: holiday?.event_status ?? STATUS.ACTIVE,
    }
}

/**
 * Holiday ids are lower case with no spaces around them (the applause create
 * form). A holiday ID is its pipeline's event type, so the ends are trimmed
 * as an event type's are (zero-width characters too).
 */
export const normaliseHolidayId = (value) =>
    normalizeEventType(value).toLowerCase()

/** The holiday ID's space error under the field (helpers/eventType), or ''. */
export const holidayIdError = (value) =>
    eventTypeError(value, { label: 'Holiday ID', example: 'diwali_2026' })

/**
 * The first problem with a create or edit form, or '' when it can be saved.
 *
 * Rules from the applause app: id and name required (id only when
 * creating); comms 1 to 7 days before; comms time 07:00 to 23:59; a reminder
 * comes after the comms (fewer days before the holiday); nothing in the past;
 * a new id must not already be a holiday or a pipeline event type.
 * When editing, an event left exactly as stored is not checked against the
 * clock (the applause app refused to rename a holiday once its first comms
 * had gone out).
 *
 * @param {object} form          see emptyHolidayForm
 * @param {object} [opts]
 * @param {number} [opts.now]    epoch seconds (tests pass a fixed clock)
 * @param {boolean} [opts.creating]
 * @param {string[]} [opts.takenIds]  existing holiday ids and event types
 * @param {object} [opts.original]    the stored holiday, when editing
 */
export const validateHoliday = (
    form,
    {
        now = Date.now() / 1000,
        creating = false,
        takenIds = [],
        original = null,
    } = {}
) => {
    if (creating && !normaliseHolidayId(form.holiday_id))
        return 'Enter a holiday ID.'
    if (creating && holidayIdError(form.holiday_id))
        return holidayIdError(form.holiday_id)
    if (!(form.holiday_name || '').trim()) return 'Enter the holiday name.'
    const holidayDate = istMidnightEpoch(form.holiday_date)
    if (holidayDate === null) return 'Pick the date of the holiday.'
    const unchanged = (time, days) =>
        (original?.events ?? []).some(
            (e) =>
                Number(e.event_time) === time &&
                Number(e.days_before_holiday) === days
        )
    const past = (days, seconds) => {
        const time = holidayDate - days * DAY + seconds
        return time < now && !unchanged(time, days)
    }
    const commsDays = Number(form.comms_days)
    if (!Number.isInteger(commsDays) || commsDays < 1)
        return 'Comms must go out at least 1 day before the holiday.'
    if (commsDays > MAX_COMMS_DAYS)
        return `Comms can only be sent within ${MAX_COMMS_DAYS} days of the holiday.`
    const commsSeconds = timeToSeconds(form.comms_time)
    if (commsSeconds === null) return 'Pick the time to send the comms.'
    if (commsSeconds < timeToSeconds(EARLIEST_COMMS_TIME))
        return `Comms can be sent from ${EARLIEST_COMMS_TIME} onwards.`
    if (past(commsDays, commsSeconds))
        return 'The comms date and time is in the past.'
    if (form.reminder_on) {
        const reminderDays = Number(form.reminder_days)
        if (!Number.isInteger(reminderDays) || reminderDays < 1)
            return 'The reminder must go out at least 1 day before the holiday.'
        if (reminderDays >= commsDays)
            return 'The reminder must come after the comms: fewer days before the holiday.'
        if (past(reminderDays, commsSeconds))
            return 'The reminder date and time is in the past.'
    }
    if (creating) {
        const id = normaliseHolidayId(form.holiday_id)
        // Trimmed both sides: a saved " Diwali_2026" pipeline is taken too.
        if (takenIds.some((t) => normaliseHolidayId(String(t)) === id))
            return `A holiday or pipeline with the ID "${id}" already exists.`
    }
    return ''
}

/**
 * The holiday as the API stores it (POST /comms/holiday, PUT
 * /comms/holidays/edit/{id}). The edit route replaces every field, so the
 * whole holiday is always sent, allowed_countries included.
 *
 * When editing, pass the stored holiday as `original`: job-holiday-reminder
 * marks an event it has sent with `alerts_arn_status`, and an event whose
 * time is unchanged keeps that mark, so an edit never sends it again.
 */
export const holidayPayload = (form, original = null) => {
    const holidayDate = istMidnightEpoch(form.holiday_date)
    const sent = (original?.events ?? []).filter((e) => e.alerts_arn_status)
    const keepMark = (event) => {
        const same = sent.find(
            (e) =>
                Number(e.event_time) === event.event_time &&
                Number(e.days_before_holiday) === event.days_before_holiday
        )
        return same
            ? { ...event, alerts_arn_status: same.alerts_arn_status }
            : event
    }
    return {
        // As typed on create (lower-cased by the form); as stored on edit.
        holiday_id: (form.holiday_id || '').trim(),
        holiday_name: form.holiday_name.trim(),
        event_status: form.event_status ?? STATUS.ACTIVE,
        holiday_date: holidayDate,
        events: buildEvents({
            holidayDate,
            commsSeconds: timeToSeconds(form.comms_time),
            commsDays: form.comms_days,
            reminderDays: form.reminder_on ? form.reminder_days : null,
        }).map(keepMark),
        allowed_countries: form.allowed_countries ?? [],
    }
}

/** The status a holiday moves to from the list's "Mark ..." action. */
export const toggledStatus = (status) =>
    status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE

export const BULK_REQUIRED_COLUMNS = [
    'holiday_id',
    'holiday_name',
    'holiday_date',
    'comms_time',
    'days_before_comms',
]
export const BULK_OPTIONAL_COLUMNS = [
    'days_before_reminder',
    'allowed_countries',
]

/**
 * Bulk upload: CSV rows (objects keyed by header, utils/csv parseCSV) to the
 * POST /comms/bulk-holiday list, plus row problems.
 *
 * Columns as in the applause app: holiday_id, holiday_name, holiday_date
 * (DD/MM/YYYY), comms_time (HH:MM), days_before_comms, and optionally
 * days_before_reminder and allowed_countries (comma separated codes). Ids
 * are kept as written (the applause bulk upload did not lower-case them).
 * Columns are read by header name rather than position.
 *
 * Each row must also pass the rules of a single new holiday (validateHoliday
 * with `creating`: 1 to 7 days, comms from 07:00, a reminder after the comms,
 * nothing in the past), and its id may be neither repeated in the file nor
 * an existing holiday's id, any status, ignoring case: the API checks only
 * active holidays and overwrites the rest (BE services/holidays_service.py,
 * repositories/holidays_repository.py put_item). Pipeline event types are
 * not refused here: a holiday's pipeline may be made before its upload.
 *
 * @param {string[]} headers
 * @param {object[]} rows
 * @param {object} [opts]
 * @param {string[]} [opts.existingIds]  every stored holiday's id
 * @param {number} [opts.now]  epoch seconds (tests pass a fixed clock)
 * @returns {{ holidays: object[], errors: string[] }}
 */
export const parseBulkHolidays = (
    headers,
    rows,
    { existingIds = [], now = Date.now() / 1000 } = {}
) => {
    const missing = BULK_REQUIRED_COLUMNS.filter((c) => !headers.includes(c))
    if (missing.length)
        return {
            holidays: [],
            errors: [`Missing required columns: ${missing.join(', ')}`],
        }
    const holidays = []
    const errors = []
    const existing = new Set(existingIds.map((id) => String(id).toLowerCase()))
    // Lower-cased id -> the line it first appeared on.
    const seen = new Map()
    rows.forEach((row, i) => {
        const line = i + 2 // the header is line 1
        const blank = BULK_REQUIRED_COLUMNS.filter(
            (c) => !(row[c] || '').trim()
        )
        if (blank.length) {
            errors.push(`Row ${line}: ${blank.join(', ')} empty`)
            return
        }
        const iso = dmyToIso(row.holiday_date)
        const commsSeconds = timeToSeconds(row.comms_time)
        const commsDays = Number(row.days_before_comms)
        const reminderRaw = (row.days_before_reminder || '').trim()
        const reminderDays = reminderRaw === '' ? null : Number(reminderRaw)
        if (!iso) {
            errors.push(`Row ${line}: holiday_date must be DD/MM/YYYY`)
            return
        }
        if (commsSeconds === null) {
            errors.push(`Row ${line}: comms_time must be HH:MM`)
            return
        }
        if (!Number.isInteger(commsDays) || commsDays < 0) {
            errors.push(`Row ${line}: days_before_comms must be a whole number`)
            return
        }
        if (
            reminderDays !== null &&
            (!Number.isInteger(reminderDays) || reminderDays < 0)
        ) {
            errors.push(
                `Row ${line}: days_before_reminder must be a whole number`
            )
            return
        }
        const id = normalizeEventType(row.holiday_id)
        const key = id.toLowerCase()
        if (seen.has(key)) {
            errors.push(
                `Row ${line}: holiday_id "${id}" is repeated (first on row ${seen.get(
                    key
                )})`
            )
            return
        }
        seen.set(key, line)
        if (existing.has(key)) {
            errors.push(`Row ${line}: "${id}" is already a holiday`)
            return
        }
        const problem = validateHoliday(
            {
                holiday_id: id,
                holiday_name: row.holiday_name,
                holiday_date: iso,
                comms_days: String(commsDays),
                comms_time: row.comms_time.trim(),
                reminder_on: reminderDays !== null,
                reminder_days:
                    reminderDays === null ? '' : String(reminderDays),
            },
            { creating: true, now }
        )
        if (problem) {
            errors.push(`Row ${line}: ${problem}`)
            return
        }
        const holidayDate = istMidnightEpoch(iso)
        holidays.push({
            holiday_id: id,
            holiday_name: row.holiday_name.trim(),
            event_status: STATUS.ACTIVE,
            holiday_date: holidayDate,
            events: buildEvents({
                holidayDate,
                commsSeconds,
                commsDays,
                reminderDays,
            }),
            allowed_countries: (row.allowed_countries || '')
                .split(',')
                .map((c) => c.trim())
                .filter(Boolean),
        })
    })
    return { holidays, errors }
}

/**
 * The role that may create, edit and switch holidays on this host, as in the
 * applause app: comms-tool-dev on dev, comms-tool-prod on production, none
 * elsewhere.
 *
 * @param {object} env  getEnvironmentVariables()
 */
export const holidayManagerRole = (env) => commsManagerRole(env)

/** True when `permissions` may change holidays on this host. */
export const canManageHolidays = (permissions, env) =>
    canManageComms(permissions, env)
